<template>
  <div class="spending-order-list">
    <div class="filter-bar">
      <!-- G10: 待办筛选 -->
      <el-radio-group v-model="filter.mode" size="small" @change="onModeChange">
        <el-radio-button label="ALL">全部</el-radio-button>
        <el-radio-button label="TODO">待我处理</el-radio-button>
        <el-radio-button label="MINE">我创建的</el-radio-button>
      </el-radio-group>
      <!-- 来源筛选 (仅 ADMIN/ASSISTANT 可见) -->
      <source-filter v-model="filter.source" @change="reload" />
      <el-select v-model="filter.status" placeholder="状态" clearable size="small" style="width:160px" @change="reload">
        <el-option label="全部" value="" />
        <el-option label="草稿 DRAFT" value="DRAFT" />
        <el-option label="收集材料中 COLLECTING_MATERIALS" value="COLLECTING_MATERIALS" />
        <el-option label="AI 审核中 AI_AUDITING" value="AI_AUDITING" />
        <el-option label="人工审核中 MANUAL_AUDITING" value="MANUAL_AUDITING" />
        <el-option label="已通过 APPROVED" value="APPROVED" />
        <el-option label="已驳回 REJECTED" value="REJECTED" />
        <el-option label="已结算 SETTLED" value="SETTLED" />
      </el-select>
      <el-button type="primary" icon="el-icon-plus" size="small" @click="$emit('create')">
        新建{{ typeLabel }}结算单
      </el-button>
      <el-button icon="el-icon-refresh" size="small" @click="reload">刷新</el-button>
      <span class="filter-tip">共 {{ total }} 条</span>
    </div>

    <el-table :data="list" v-loading="loading" stripe border size="small" class="app-table" style="width:100%" header-align="center" :default-sort="{ prop: 'createTime', order: 'descending' }">
      <el-table-column prop="id" label="#" width="60" align="center" header-align="center" />
      <el-table-column prop="orderNo" label="结算单号" min-width="180" show-overflow-tooltip align="center" header-align="center">
        <template slot-scope="{ row }">
          {{ row.orderNo }}
          <source-badge :source="row.source" />
        </template>
      </el-table-column>
      <el-table-column prop="projectName" label="关联项目" min-width="160" show-overflow-tooltip align="center" header-align="center">
        <template slot-scope="{ row }">{{ row.projectName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="orgName" label="所属机构" min-width="140" show-overflow-tooltip align="center" header-align="center">
        <template slot-scope="{ row }">{{ row.orgName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="meetingName" label="关联会议" min-width="140" show-overflow-tooltip align="center" header-align="center">
        <template slot-scope="{ row }">{{ row.meetingName || '-' }}</template>
      </el-table-column>
      <el-table-column v-if="settlementType === 'SUPPLIER'" prop="supplierOrgName" label="供应商" min-width="140" show-overflow-tooltip align="center" header-align="center">
        <template slot-scope="{ row }">{{ row.supplierOrgName || row.supplierName || '-' }}</template>
      </el-table-column>
      <el-table-column prop="orderAmount" label="金额" width="120" align="center" header-align="center">
        <template slot-scope="{ row }">
          <span class="amount">¥{{ Number(row.orderAmount || 0).toFixed(2) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="主状态" width="120" align="center" header-align="center">
        <template slot-scope="{ row }">
          <el-tag :type="statusType(row.status)" size="mini">{{ statusLabel(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="审核阶段" width="240" align="center" header-align="center">
        <template slot-scope="{ row }">
          <div class="stage-flow">
            <span :class="['stage', stageClass(row.supplierStatus)]" title="供应商">供</span>
            <i class="el-icon-arrow-right" />
            <span :class="['stage', stageClass(row.aiStatus)]" title="AI 审核">AI</span>
            <i class="el-icon-arrow-right" />
            <span :class="['stage', stageClass(row.officialStatus)]" title="官员审核">官</span>
            <i class="el-icon-arrow-right" />
            <span :class="['stage', stageClass(row.financeStatus)]" title="财务审核">财</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="170" align="center" header-align="center">
        <template slot-scope="{ row }">{{ formatDateTime(row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="140" align="center" header-align="center" fixed="right" class-name="col-actions">
        <template slot-scope="{ row }">
          <div class="action-row">
            <el-button type="text" size="small" @click="$emit('detail', row)">详情</el-button>
            <el-divider v-if="canSubmit(row)" direction="vertical" />
            <el-button v-if="canSubmit(row)" type="text" size="small" style="color:#67C23A" @click="$emit('submit', row)">
              提交
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="app-pagination" v-if="total > pageSize">
      <el-pagination
        background
        layout="prev, pager, next, total"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="pageNum"
        @current-change="reload" />
    </div>
  </div>
</template>

<script>
import { getSpendingOrderList } from '@/request/api.js';
import SourceBadge from '@/components/SourceBadge.vue';
import SourceFilter from '@/components/SourceFilter.vue';

const STATUS_TYPE = {
  DRAFT: 'info', SUBMITTED: '', AI_AUDITING: 'warning', MANUAL_AUDITING: 'warning',
  APPROVED: 'success', REJECTED: 'danger', SETTLED: 'success'
};
const STATUS_LABEL = {
  DRAFT: '草稿', SUBMITTED: '已提交', AI_AUDITING: 'AI审核中', MANUAL_AUDITING: '人工审核中',
  APPROVED: '已通过', REJECTED: '已驳回', SETTLED: '已结算'
};

export default {
  name: 'SpendingOrderList',
  components: { SourceBadge, SourceFilter },
  props: {
    settlementType: { type: String, required: true } // SUPPLIER / LABOR
  },
  data() {
    return {
      list: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
      loading: false,
      filter: { status: '', mode: 'ALL', source: '' }
    };
  },
  computed: {
    typeLabel() {
      return this.settlementType === 'LABOR' ? '劳务' : '供应商';
    }
  },
  watch: {
    settlementType() {
      this.pageNum = 1;
      this.reload();
    }
  },
  created() {
    this.reload();
  },
  methods: {
    statusType(s) { return STATUS_TYPE[s] || 'info'; },
    statusLabel(s) { return STATUS_LABEL[s] || s || '-'; },
    stageClass(s) {
      if (['APPROVED', 'PASSED', 'SUBMITTED'].indexOf(s) >= 0) return 'done';
      if (['REJECTED', 'FAILED'].indexOf(s) >= 0) return 'fail';
      return 'pending';
    },
    canSubmit(row) { return ['DRAFT', 'REJECTED'].indexOf(row.status) >= 0; },

    // 把 "2026-05-23T10:49:53.000+00:00" / ISO8601 / 毫秒时间戳 统一格式化为 "YYYY-MM-DD HH:mm:ss"
    formatDateTime(t) {
      if (!t) return '-';
      const d = (typeof t === 'number') ? new Date(t) : new Date(String(t));
      if (isNaN(d.getTime())) return String(t);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },

    onModeChange() {
      this.pageNum = 1;
      this.reload();
    },

    async reload() {
      this.loading = true;
      try {
        const resp = await getSpendingOrderList({
          page: this.pageNum,
          size: this.pageSize,
          settlementType: this.settlementType,
          status: this.filter.status || undefined,
          mode: this.filter.mode || 'ALL',
          source: this.filter.source || undefined
        });
        if (resp.code === 200) {
          this.list = resp.data.records || [];
          this.total = resp.data.total || 0;
        }
      } catch (e) {
        this.$message.error('加载失败');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.filter-bar {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 12px; padding: 8px 0;
}
.filter-tip { color: #909399; font-size: 12px; margin-left: auto; }
.amount { color: #E6A23C; font-weight: 600; }
.stage-flow {
  display: inline-flex; align-items: center; gap: 4px;
}
.stage {
  display: inline-block; width: 24px; height: 22px; line-height: 22px;
  font-size: 12px; text-align: center; border-radius: 3px;
  background: #F4F4F5; color: #909399;
}
.stage.done { background: #F0F9EB; color: #67C23A; }
.stage.fail { background: #FEF0F0; color: #F56C6C; }
.stage.pending { background: #FDF6EC; color: #E6A23C; }

/* 操作列: 给按钮之间分隔, 避免拥挤 */
.action-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
}
.action-row .el-button + .el-button { margin-left: 0; }
.action-row .el-divider--vertical { margin: 0 2px; }
</style>
